/**
 * @class BaseModal
 * @extends HTMLElement
 *
 * Base popup element.
 *
 * @example
 * <!-- Trigger -->
 * <button type="button" class="button" aria-controls="TestModal">
 *   Open modal
 * </button>
 *
 * <!-- Modal -->
 * <base-modal
 *   class="modal"
 *   id="TestModal"
 *   role="dialog"
 *   aria-modal="true"
 *   aria-labelledby="TestModal-Heading"
 * >
 *   <div class="overlay" aria-controls="TestModal" tabindex="-1"></div>
 *
 *   <div class="modal__content">
 *     <div class="modal__header">
 *       <h2 id="TestModal-Heading">Modal title</h2>
 *
 *       <button type="button" class="modal__close-button" aria-controls="TestModal">
 *         <span class="visually-hidden">Close modal</span>
 *       </button>
 *     </div>
 *
 *     <div class="modal__body">Modal body content</div>
 *   </div>
 * </base-modal>
 */

const openModals = new Set();

export class BaseModal extends HTMLElement {
  #originalParentElement = null;
  #nextSiblingElement = null;
  #previouslyFocusedElement = null;
  #triggerElement = null;

  #isTransitioning = false;

  /**
   * When true, the modal is appended to `<body>` on open to ensure
   * correct stacking context.
   *
   * @returns {boolean}
   */
  get shouldAppendToBody() {
    return this.hasAttribute('should-append-to-body');
  }

  /**
   * When true, the modal remains at `<body>` after first open.
   * Prevents media elements from reloading when moved in the DOM.
   *
   * @returns {boolean}
   */
  get persistAtBody() {
    return false;
  }

  /**
   * When true, opening modal locks the body scroll.
   *
   * @returns {boolean}
   */
  get shouldLockScroll() {
    return !this.hasAttribute('no-scroll-lock');
  }

  get triggerElement() {
    return this.#triggerElement;
  }

  get isOpen() {
    return this.hasAttribute('open');
  }

  get isTransitioning() {
    return this.#isTransitioning;
  }

  connectedCallback() {
    document.addEventListener('keydown', this.#onKeyDown);
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this.#onKeyDown);
  }

  async open() {
    if (this.isOpen || this.#isTransitioning) return;

    this.#isTransitioning = true;

    try {
      this.#previouslyFocusedElement = document.activeElement;

      this.removeAttribute('inert');

      if (this.shouldAppendToBody && this.parentNode !== document.body) {
        if (!this.persistAtBody) {
          this.#originalParentElement = this.parentNode;
          this.#nextSiblingElement = this.nextSibling;
        }

        document.body.appendChild(this);
        await new Promise((resolve) => requestAnimationFrame(resolve));
      }

      this.setAttribute('open', '');

      if (this.shouldLockScroll) {
        openModals.add(this);
        if (openModals.size === 1) {
          document.body.style.overflow = 'hidden';
        }
      }

      this.onOpen();
    } finally {
      this.#isTransitioning = false;
    }
  }

  async close() {
    if (!this.isOpen || this.#isTransitioning) return;

    this.#isTransitioning = true;

    try {
      this.removeAttribute('open');

      if (this.shouldLockScroll) {
        openModals.delete(this);
        if (openModals.size === 0) {
          document.body.style.overflow = '';
        }
      }

      this.onClose();

      await this.#awaitTransition();

      this.setAttribute('inert', '');

      if (this.shouldAppendToBody && !this.persistAtBody) {
        this.#restorePosition();
      }

      this.#previouslyFocusedElement?.focus();
      this.#previouslyFocusedElement = null;

      this.onAfterClose();
    } finally {
      this.#isTransitioning = false;
    }
  }

  toggle(triggerElement = null) {
    if (!this.isOpen) {
      this.#triggerElement = triggerElement;
    }

    this.isOpen ? this.close() : this.open();
  }

  onOpen() {}
  onClose() {}
  onAfterClose() {}

  #restorePosition() {
    if (!this.#originalParentElement) return;

    if (this.#nextSiblingElement) {
      this.#originalParentElement.insertBefore(this, this.#nextSiblingElement);
    } else {
      this.#originalParentElement.appendChild(this);
    }

    this.#originalParentElement = null;
    this.#nextSiblingElement = null;
  }

  #awaitTransition() {
    return new Promise((resolve) => {
      const handler = (event) => {
        if (event.target !== this) return;
        this.removeEventListener('transitionend', handler);
        resolve();
      };

      this.addEventListener('transitionend', handler);

      setTimeout(resolve, 350);
    });
  }

  #onKeyDown = (event) => {
    if (event.key === 'Escape' && this.isOpen) {
      this.close();
    }
  };
}

if (!customElements.get('base-modal')) {
  customElements.define('base-modal', BaseModal);
}

/**
 * Global click handler to trigger modals based on aria-controls attributes.
 */
document.addEventListener ("DOMContentLoaded", () => {
  document.addEventListener("click", (event) => {
    const modalTrigger = event.target.closest("[aria-controls]");

    if (!modalTrigger) {
      return;
    }

    const modalId = modalTrigger.getAttribute("aria-controls");
    const modal = document.getElementById(modalId);

    if (!(modal instanceof BaseModal)) {
      return;
    }

    event.preventDefault();
    modal.toggle(modalTrigger);
  });
});
